/**
 * Barbie theme manifest.
 *
 * Pink-dominant feminine palette with hot-pink → soft-pink gradient and
 * gold sparkle accents. Typography pairs Playfair Display for headings with
 * Inter for body copy (Requirement 7.2).
 *
 * Requirement compliance:
 * - 7.1 — exposes `accentPink` (#ff1493).
 * - 7.2 — paired display/body typography.
 * - 7.3 / 8.4 / 8.6 — animation durations within 150–600 ms / ≤600 ms / ≤1500 ms.
 * - 8.1 — selectable as a built-in theme; default `activeThemeId`.
 * - 8.7 — id is the persistence key.
 * - 8.8 — IP-safe: art is original abstract feminine illustration (heart-tiara
 *   silhouette, sparkles), NOT Mattel's Barbie character or trademarks.
 * - 10.1 / 10.4 — `textPrimary` on `surface` and on `bubbleAssistant` meets
 *   WCAG 2.1 AA contrast; `focusRing` meets the 3:1 non-text contrast bar.
 *
 * Pure module: importing this manifest has no runtime side effects. The SVG
 * art is fetched lazily via Vite's `?url` thunk imports, which are split into
 * a separate chunk per theme (Property 28 / Requirement 11.5).
 */

import type { ThemeManifest } from '../types';

export const barbieManifest: ThemeManifest = {
  id: 'barbie',
  displayName: { tr: 'Barbie', en: 'Barbie', el: 'Barbie' },
  colors: {
    bgGradient: ['#ff5d9e', '#ffaad4'],
    surface: '#fff5fa',
    textPrimary: '#1a0d14',
    textSecondary: '#6b3a55',
    accentPink: '#ff1493',
    accentSecondary: '#ffd700',
    bubbleUser: '#ff5d9e',
    bubbleAssistant: '#fff5fa',
    focusRing: '#ff1493',
  },
  typography: {
    display: '"Playfair Display", Georgia, serif',
    body: '"Inter", "Segoe UI", system-ui, sans-serif',
  },
  assets: {
    backgroundArt: () => import('./background.svg?url'),
    characterArt: () => import('./barbie.png?url'),
  },
  animation: {
    bubbleIn: {
      duration: 240,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      type: 'transform',
    },
    characterIdle: {
      duration: 1200,
      easing: 'ease-in-out',
      type: 'transform',
      loop: true,
      enabled: true,
    },
    crossfadeMs: 480,
  },
};
